import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { formatClinicalEntry, formatDateOnly, errorWords } from "@alm/domain";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowDown, ArrowUp } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import {
  ClinicalForm,
  clinicalToForm,
  diagnosisToInput,
  EMPTY_CLINICAL,
  problemToInput,
} from "../-components/clinical-forms";
import { ReadGate } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/read-gate";
import { StatusBadge } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/status-badge";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/clinical")({
  component: ClinicalTab,
});

const fail = (error: Error) => toast.error(errorWords(error));

type DialogState = { kind: "problem" | "diagnosis"; id?: string } | null;

function ClinicalTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.clinical:update", resident.facilityId);
  const queryClient = useQueryClient();
  const [includeResolved, setIncludeResolved] = useState(true);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [removing, setRemoving] = useState<{
    title: string;
    noun: string;
    run: () => void;
  } | null>(null);
  const input = { residentId, includeResolved };
  const problems = useQuery(orpc.care.residents.problems.list.queryOptions({ input }));
  const diagnoses = useQuery(orpc.care.residents.diagnoses.list.queryOptions({ input }));
  const done = (message: string) => async () => {
    toast.success(message);
    setDialog(null);
    await queryClient.invalidateQueries({ queryKey: orpc.care.residents.key() });
  };
  const m = {
    problemCreate: useMutation(
      orpc.care.residents.problems.create.mutationOptions({
        onSuccess: done("Problem added"),
        onError: fail,
      }),
    ),
    problemUpdate: useMutation(
      orpc.care.residents.problems.update.mutationOptions({
        onSuccess: done("Problem updated"),
        onError: fail,
      }),
    ),
    problemRemove: useMutation(
      orpc.care.residents.problems.remove.mutationOptions({
        onSuccess: done("Problem removed"),
        onError: fail,
      }),
    ),
    diagnosisCreate: useMutation(
      orpc.care.residents.diagnoses.create.mutationOptions({
        onSuccess: done("Diagnosis added"),
        onError: fail,
      }),
    ),
    diagnosisUpdate: useMutation(
      orpc.care.residents.diagnoses.update.mutationOptions({
        onSuccess: done("Diagnosis updated"),
        onError: fail,
      }),
    ),
    diagnosisRemove: useMutation(
      orpc.care.residents.diagnoses.remove.mutationOptions({
        onSuccess: done("Diagnosis removed"),
        onError: fail,
      }),
    ),
    reorder: useMutation(
      orpc.care.residents.diagnoses.reorder.mutationOptions({
        onSuccess: done("Order updated"),
        onError: fail,
      }),
    ),
  };
  const pending = Object.values(m).some((mutation) => mutation.isPending);
  const editingProblem =
    dialog?.kind === "problem" && dialog.id
      ? problems.data?.find((row) => row.id === dialog.id)
      : undefined;
  const editingDiagnosis =
    dialog?.kind === "diagnosis" && dialog.id
      ? diagnoses.data?.find((row) => row.id === dialog.id)
      : undefined;

  const move = (index: number, direction: -1 | 1) => {
    const rows = diagnoses.data ?? [];
    const target = rows[index + direction];
    const current = rows[index];
    if (!target || !current) return;
    const ids = rows.map((row) => row.id);
    ids[index] = target.id;
    ids[index + direction] = current.id;
    m.reorder.mutate({ residentId, diagnosisIds: ids });
  };

  const actions = (onEdit: () => void, onRemove: () => void, extra?: React.ReactNode) =>
    canWrite ? (
      <>
        {extra}
        <Button size="sm" variant="ghost" onClick={onEdit}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Switch id="show-resolved" checked={includeResolved} onCheckedChange={setIncludeResolved} />
        <Label htmlFor="show-resolved" className="font-normal">
          Show resolved
        </Label>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordCard
          title="Problem list"
          count={problems.data?.length}
          canWrite={canWrite}
          onAdd={() => setDialog({ kind: "problem" })}
        >
          {problems.isPending || problems.isError ? (
            <ReadGate query={problems} noun="problem list" />
          ) : !problems.data.length ? (
            <EmptyRows text="No problems recorded." />
          ) : (
            <ul className="divide-y">
              {problems.data.map((row) => (
                <RecordRow
                  key={row.id}
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      {formatClinicalEntry(row)}
                      <StatusBadge status={row.status} />
                    </span>
                  }
                  meta={
                    <>
                      {row.onsetDate ? `Onset ${formatDateOnly(row.onsetDate)}` : "Onset unknown"}
                      {row.resolvedDate ? `, resolved ${formatDateOnly(row.resolvedDate)}` : ""}
                      {row.notes ? `, ${row.notes}` : ""}
                    </>
                  }
                  actions={actions(
                    () => setDialog({ kind: "problem", id: row.id }),
                    () =>
                      setRemoving({
                        title: `Delete ${formatClinicalEntry(row)}?`,
                        noun: "problem",
                        run: () => m.problemRemove.mutate({ problemId: row.id }),
                      }),
                  )}
                />
              ))}
            </ul>
          )}
        </RecordCard>
        <RecordCard
          title="Diagnoses"
          count={diagnoses.data?.length}
          canWrite={canWrite}
          onAdd={() => setDialog({ kind: "diagnosis" })}
        >
          {diagnoses.isPending || diagnoses.isError ? (
            <ReadGate query={diagnoses} noun="diagnoses" />
          ) : !diagnoses.data.length ? (
            <EmptyRows text="No diagnoses recorded." />
          ) : (
            <ul className="divide-y">
              {diagnoses.data.map((row, index) => (
                <RecordRow
                  key={row.id}
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      {index === 0 ? <Badge variant="outline">Primary</Badge> : null}
                      {formatClinicalEntry(row)}
                      <StatusBadge status={row.status} />
                    </span>
                  }
                  meta={
                    <>
                      {row.onsetDate ? `Onset ${formatDateOnly(row.onsetDate)}` : "Onset unknown"}
                      {row.resolvedDate ? `, resolved ${formatDateOnly(row.resolvedDate)}` : ""}
                      {row.notes ? `, ${row.notes}` : ""}
                    </>
                  }
                  actions={actions(
                    () => setDialog({ kind: "diagnosis", id: row.id }),
                    () =>
                      setRemoving({
                        title: `Delete ${formatClinicalEntry(row)}?`,
                        noun: "diagnosis",
                        run: () => m.diagnosisRemove.mutate({ diagnosisId: row.id }),
                      }),
                    <>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Move ${formatClinicalEntry(row)} up`}
                        disabled={index === 0 || includeResolved === false}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Move ${formatClinicalEntry(row)} down`}
                        disabled={
                          index === (diagnoses.data?.length ?? 0) - 1 || includeResolved === false
                        }
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                    </>,
                  )}
                />
              ))}
            </ul>
          )}
        </RecordCard>
      </div>
      {removing ? (
        <RemoveConfirm
          title={removing.title}
          description="For an entry added in error. To record that it is over, edit it and set it resolved instead."
          confirmLabel={`Delete ${removing.noun}`}
          onConfirm={removing.run}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {dialog?.id ? "Edit" : "Add"} {dialog?.kind === "problem" ? "problem" : "diagnosis"}
            </DialogTitle>
          </DialogHeader>
          {dialog?.kind === "problem" ? (
            <ClinicalForm
              kind="problem"
              key={editingProblem?.id ?? "new-problem"}
              pending={pending}
              defaultValues={editingProblem ? clinicalToForm(editingProblem) : EMPTY_CLINICAL}
              onCancel={() => setDialog(null)}
              onSubmit={(values) =>
                editingProblem
                  ? m.problemUpdate.mutate({
                      problemId: editingProblem.id,
                      ...problemToInput(values),
                    })
                  : m.problemCreate.mutate({ residentId, ...problemToInput(values) })
              }
            />
          ) : null}
          {dialog?.kind === "diagnosis" ? (
            <ClinicalForm
              kind="diagnosis"
              key={editingDiagnosis?.id ?? "new-diagnosis"}
              pending={pending}
              defaultValues={editingDiagnosis ? clinicalToForm(editingDiagnosis) : EMPTY_CLINICAL}
              onCancel={() => setDialog(null)}
              onSubmit={(values) =>
                editingDiagnosis
                  ? m.diagnosisUpdate.mutate({
                      diagnosisId: editingDiagnosis.id,
                      ...diagnosisToInput(values),
                    })
                  : m.diagnosisCreate.mutate({ residentId, ...diagnosisToInput(values) })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
